import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, collection, addDoc, Timestamp, onSnapshot, query, where, doc, updateDoc, deleteDoc, getDoc, getDocs } from 'firebase/firestore';
import { FaMapMarkerAlt, FaTasks } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

type ViewType = 'día' | 'semana' | 'mes';

const getToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

function getMonthDays(year: number, month: number) {
  const lastDay = new Date(year, month + 1, 0);
  const days = [];
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push(new Date(year, month, d));
  }
  return days;
}

function getWeekDays(date: Date) {
  const start = new Date(date);
  start.setDate(date.getDate() - date.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

// Función para saber si un evento ocurre en un día dado
function eventOccursOnDay(ev: any, day: Date) {
  if (!ev.startDate || !ev.endDate) return false;
  const start = getEventDate(ev.startDate);
  const end = getEventDate(ev.endDate);
  if (!start || !end) return false;
  // Normaliza a solo fecha
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
  const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999);
  return start <= dayEnd && end >= dayStart;
}

// Corrige la obtención de fechas en getEventColumnGroups para soportar tasks y eventos Firestore
function getEventDate(val: any) {
  if (!val) return undefined;
  if (typeof val.toDate === 'function') return val.toDate();
  if (typeof val === 'string' || typeof val === 'number') return new Date(val);
  if (val instanceof Date) return val;
  return undefined;
}

function getEventColumnGroups(events: any[]) {
  // Filtra eventos sin fechas válidas
  const filtered = events.filter(ev => getEventDate(ev.startDate) && getEventDate(ev.endDate));
  // Ordena por hora de inicio
  const sorted = [...filtered].sort((a, b) => getEventDate(a.startDate).getTime() - getEventDate(b.startDate).getTime());
  const groups = [];
  let currentGroup = [];
  let lastEnd = 0;
  for (let i = 0; i < sorted.length; i++) {
    const ev = sorted[i];
    const start = getEventDate(ev.startDate).getTime();
    const end = getEventDate(ev.endDate).getTime();
    if (currentGroup.length === 0 || start < lastEnd) {
      currentGroup.push(ev);
      lastEnd = Math.max(lastEnd || 0, end);
    } else {
      groups.push(currentGroup);
      currentGroup = [ev];
      lastEnd = end;
    }
  }
  if (currentGroup.length > 0) groups.push(currentGroup);
  // Asigna columnas dentro de cada grupo
  groups.forEach(group => {
    const columns = [];
    for (let i = 0; i < group.length; i++) {
      const ev = group[i];
      const start = getEventDate(ev.startDate).getTime();
      const end = getEventDate(ev.endDate).getTime();
      let col = 0;
      while (columns[col] && columns[col] > start) col++;
      ev._column = col;
      columns[col] = end;
    }
    group.forEach(ev => ev._totalColumns = columns.length);
  });
  return sorted;
}

// Parser robusto para fechas en español tipo '25 de julio de 2025, 7:00:00 a.m. UTC-5'
function parseSpanishDate(str: string) {
  if (typeof str !== 'string') return undefined;
  // Reemplaza meses en español por número
  const meses = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];
  let regex = /^(\d{1,2}) de ([a-záéíóúñ]+) de (\d{4}), (\d{1,2}):(\d{2}):(\d{2})\s*(a\.m\.|p\.m\.) UTC([+-]\d+)/i;
  let match = str.match(regex);
  if (match) {
    let [_, dia, mesStr, anio, hora, min, seg, ampm, tz] = match;
    let mes = meses.indexOf(mesStr.toLowerCase());
    if (mes === -1) return undefined;
    let h = parseInt(hora, 10);
    if (ampm.toLowerCase().includes('p') && h < 12) h += 12;
    if (ampm.toLowerCase().includes('a') && h === 12) h = 0;
    let fecha = new Date(Date.UTC(parseInt(anio), mes, parseInt(dia), h, parseInt(min), parseInt(seg)));
    // Ajusta zona horaria
    let offset = parseInt(tz, 10);
    fecha.setUTCHours(fecha.getUTCHours() - offset);
    return fecha;
  }
  // Intenta parsear sin segundos
  regex = /^(\d{1,2}) de ([a-záéíóúñ]+) de (\d{4}), (\d{1,2}):(\d{2})\s*(a\.m\.|p\.m\.) UTC([+-]\d+)/i;
  match = str.match(regex);
  if (match) {
    let [_, dia, mesStr, anio, hora, min, ampm, tz] = match;
    let mes = meses.indexOf(mesStr.toLowerCase());
    if (mes === -1) return undefined;
    let h = parseInt(hora, 10);
    if (ampm.toLowerCase().includes('p') && h < 12) h += 12;
    if (ampm.toLowerCase().includes('a') && h === 12) h = 0;
    let fecha = new Date(Date.UTC(parseInt(anio), mes, parseInt(dia), h, parseInt(min), 0));
    let offset = parseInt(tz, 10);
    fecha.setUTCHours(fecha.getUTCHours() - offset);
    return fecha;
  }
  return undefined;
}

const BusinessAgenda: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const [view, setView] = useState<ViewType>('mes');
  const [currentDate, setCurrentDate] = useState<Date>(getToday());
  const [showModal, setShowModal] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const db = getFirestore();
  const [events, setEvents] = useState<any[]>([]);
  // Estado para forzar re-render al cambiar idioma
  const [langKey, setLangKey] = useState(0);
  const [eventStartDate, setEventStartDate] = useState(() => {
    const d = new Date(currentDate);
    d.setHours(12,0,0,0);
    return d.toISOString().slice(0,16);
  });
  const [eventEndDate, setEventEndDate] = useState(() => {
    const d = new Date(currentDate);
    d.setHours(13,0,0,0);
    return d.toISOString().slice(0,16);
  });
  const [allDay, setAllDay] = useState(false);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [eventLocation, setEventLocation] = useState('');
  const [assignedTasks, setAssignedTasks] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setLangKey(k => k + 1);
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, [i18n]);

  // Validar fechas
  useEffect(() => {
    if (allDay) {
      // Si es todo el día, ajusta a 00:00 y 23:59
      const start = new Date(eventStartDate);
      start.setHours(0,0,0,0);
      const end = new Date(eventEndDate);
      end.setHours(23,59,0,0);
      setEventStartDate(start.toISOString().slice(0,16));
      setEventEndDate(end.toISOString().slice(0,16));
    }
    if (new Date(eventEndDate) <= new Date(eventStartDate)) {
      setError(t('workerAgenda.invalidDateRange', 'End date must be after start date'));
    } else {
      setError('');
    }
  }, [eventStartDate, eventEndDate, allDay, t]);

  // Cargar eventos de Firestore
  useEffect(() => {
    if (!currentUser) return;
    const db = getFirestore();
    const agendaRef = collection(db, 'users', currentUser.uid, 'agenda');
    // Solo eventos del mes actual para optimizar
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
    const q = query(agendaRef, where('startDate', '>=', Timestamp.fromDate(startOfMonth)), where('startDate', '<=', Timestamp.fromDate(endOfMonth)));
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('[AGENDA] Eventos cargados:', data);
      setEvents(data);
    });
    return () => unsub();
  }, [currentUser, currentDate]);

  // Reemplaza el useEffect de carga de tasks por este:
  useEffect(() => {
    if (!currentUser) return;
    const db = getFirestore();
    const userRef = doc(db, 'users', currentUser.uid);
    getDoc(userRef).then(async userSnap => {
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const businessId = userData.businessId || userData.id || currentUser.uid;
        if (!businessId) return;
        const projectsRef = collection(db, 'projects', businessId, 'projects');
        const projectsSnap = await getDocs(projectsRef);
        const allTasks: any[] = [];
        for (const projectDoc of projectsSnap.docs) {
          const projectId = projectDoc.id;
          const tasksRef = collection(db, 'projects', businessId, 'projects', projectId, 'tasks');
          const tasksSnap = await getDocs(tasksRef);
          tasksSnap.forEach(taskDoc => {
            const data = taskDoc.data();
            let startDate: any = data.startDate;
            if (typeof startDate === 'object' && typeof startDate.toDate === 'function') {
              startDate = startDate.toDate();
            } else if (typeof startDate === 'string') {
              let parsed = new Date(startDate);
              if (isNaN(parsed.getTime())) {
                const parsedDate = parseSpanishDate(startDate);
                if (parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
                  parsed = parsedDate;
                } else {
                  // Si no se puede parsear, salta el task
                  return;
                }
              }
              if (parsed instanceof Date && !isNaN(parsed.getTime())) startDate = parsed;
            }
            if (!(startDate instanceof Date) || isNaN(startDate.getTime())) {
              return;
            }
            let startHour = 8, startMin = 0;
            if (typeof data.startTime === 'string' && /^\d{2}:\d{2}$/.test(data.startTime)) {
              const [h, m] = data.startTime.split(':').map(Number);
              startHour = h; startMin = m;
              startDate.setHours(h, m, 0, 0);
            }
            if (data.durationUnit === 'days' && typeof data.finalDuration === 'number') {
              for (let i = 0; i < data.finalDuration; i++) {
                const dayStart = new Date(startDate);
                dayStart.setDate(startDate.getDate() + i);
                dayStart.setHours(startHour, startMin, 0, 0);
                const dayEnd = new Date(dayStart);
                dayEnd.setHours(dayEnd.getHours() + 9);
                allTasks.push({
                  ...data,
                  id: taskDoc.id + '_day' + i,
                  _isTask: true,
                  businessId,
                  projectId,
                  taskId: taskDoc.id,
                  startDate: dayStart,
                  endDate: dayEnd,
                });
              }
            } else if (data.durationUnit === 'hours' && typeof data.finalDuration === 'number') {
              const endDate = new Date(startDate);
              endDate.setHours(endDate.getHours() + data.finalDuration);
              allTasks.push({
                ...data,
                id: taskDoc.id,
                _isTask: true,
                businessId,
                projectId,
                taskId: taskDoc.id,
                startDate,
                endDate,
              });
            }
          });
        }
        setAssignedTasks(allTasks);
      }
    });
  }, [currentUser]);

  // Navegación
  const goToday = () => setCurrentDate(getToday());
  const goPrev = () => {
    if (view === 'mes') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else if (view === 'semana') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 7));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 1));
    }
  };
  const goNext = () => {
    if (view === 'mes') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (view === 'semana') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 7));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1));
    }
  };

  // Etiquetas de días y vistas (dinámicas)
  const dayLabels = useMemo(() => [
    t('workerAgenda.sunday'),
    t('workerAgenda.monday'),
    t('workerAgenda.tuesday'),
    t('workerAgenda.wednesday'),
    t('workerAgenda.thursday'),
    t('workerAgenda.friday'),
    t('workerAgenda.saturday'),
  ], [t, i18n.language, langKey]);

  const viewOptions = useMemo(() => [
    { value: 'día' as ViewType, label: t('workerAgenda.day') },
    { value: 'semana' as ViewType, label: t('workerAgenda.week') },
    { value: 'mes' as ViewType, label: t('workerAgenda.month') },
  ], [t, i18n.language, langKey]);

  const CATEGORIES = [
    { value: 'red', color: 'bg-red-500', label: t('workerAgenda.categoryRed', 'Red') },
    { value: 'blue', color: 'bg-blue-500', label: t('workerAgenda.categoryBlue', 'Blue') },
    { value: 'green', color: 'bg-green-500', label: t('workerAgenda.categoryGreen', 'Green') },
    { value: 'yellow', color: 'bg-yellow-400', label: t('workerAgenda.categoryYellow', 'Yellow') },
    { value: 'purple', color: 'bg-purple-500', label: t('workerAgenda.categoryPurple', 'Purple') },
  ];
  const [eventCategory, setEventCategory] = useState('red');

  // Abrir modal para editar evento
  const handleEventClick = (ev: any) => {
    setSelectedEvent(ev);
    setEventTitle(ev.title);
    setEventDescription(ev.description);
    setEventCategory(ev.category);
    setAllDay(ev.allDay || false);
    setEventStartDate(ev.startDate ? new Date(ev.startDate.toDate()).toISOString().slice(0,16) : '');
    setEventEndDate(ev.endDate ? new Date(ev.endDate.toDate()).toISOString().slice(0,16) : '');
    setEventLocation(ev.location || '');
    setShowModal(true);
  };

  // Guardar evento (crear o editar)
  const handleSaveEvent = async () => {
    if (!currentUser) return;
    setSaving(true);
    try {
      const agendaRef = collection(db, 'users', currentUser.uid, 'agenda');
      if (selectedEvent) {
        // Editar
        const eventRef = doc(db, 'users', currentUser.uid, 'agenda', selectedEvent.id);
        await updateDoc(eventRef, {
          title: eventTitle,
          description: eventDescription,
          startDate: Timestamp.fromDate(new Date(eventStartDate)),
          endDate: Timestamp.fromDate(new Date(eventEndDate)),
          allDay,
          category: eventCategory,
          location: eventLocation,
        });
      } else {
        // Crear
        await addDoc(agendaRef, {
          title: eventTitle,
          description: eventDescription,
          startDate: Timestamp.fromDate(new Date(eventStartDate)),
          endDate: Timestamp.fromDate(new Date(eventEndDate)),
          allDay,
          category: eventCategory,
          location: eventLocation,
          createdAt: Timestamp.now(),
        });
      }
      setShowModal(false);
      setSelectedEvent(null);
      setEventTitle('');
      setEventDescription('');
      setEventCategory('red');
      setAllDay(false);
      setEventStartDate(() => {
        const d = new Date(currentDate);
        d.setHours(12,0,0,0);
        return d.toISOString().slice(0,16);
      });
      setEventEndDate(() => {
        const d = new Date(currentDate);
        d.setHours(13,0,0,0);
        return d.toISOString().slice(0,16);
      });
      setEventLocation('');
    } catch (e) {
      alert('Error al guardar el evento');
    } finally {
      setSaving(false);
    }
  };

  // Eliminar evento
  const handleDeleteEvent = async () => {
    if (!currentUser || !selectedEvent) return;
    setSaving(true);
    try {
      const eventRef = doc(db, 'users', currentUser.uid, 'agenda', selectedEvent.id);
      await deleteDoc(eventRef);
      setShowModal(false);
      setSelectedEvent(null);
      setEventTitle('');
      setEventDescription('');
      setEventCategory('red');
      setAllDay(false);
      setEventStartDate(() => {
        const d = new Date(currentDate);
        d.setHours(12,0,0,0);
        return d.toISOString().slice(0,16);
      });
      setEventEndDate(() => {
        const d = new Date(currentDate);
        d.setHours(13,0,0,0);
        return d.toISOString().slice(0,16);
      });
      setEventLocation('');
    } catch (e) {
      alert('Error al eliminar el evento');
    } finally {
      setSaving(false);
    }
  };

  const handleDayClick = (date: Date | null) => {
    if (!date) return;
    setCurrentDate(date);
    setView('día');
  };

  // Función para renderizar badge de estado
  // function renderStatusBadge(status: string) {
  //   let color = 'bg-gray-400';
  //   let label = status;
  //   if (status === 'assigned') { color = 'bg-blue-500'; label = t('workerAgenda.statusAssigned', 'Asignada'); }
  //   if (status === 'in_progress') { color = 'bg-yellow-500'; label = t('workerAgenda.statusInProgress', 'En progreso'); }
  //   if (status === 'completed') { color = 'bg-green-600'; label = t('workerAgenda.statusCompleted', 'Completada'); }
  //   return <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold text-white ${color}`}>{label}</span>;
  // }

  // Renderizado de calendario
  const renderMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const days = getMonthDays(year, month);
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    const weeks: Date[][] = [];
    let week: (Date | null)[] = Array(firstDayOfWeek).fill(null);
    days.forEach((day) => {
      week.push(day);
      if (week.length === 7) {
        weeks.push(week as Date[]);
        week = [];
      }
    });
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      weeks.push(week as Date[]);
    }
    weeks.forEach((w) => {
      w.forEach((d) => {
        if (d) {
          const tasksForDay = assignedTasks.filter(task => eventOccursOnDay(task, d));
          const eventsForDay = events.filter(ev => eventOccursOnDay(ev, d));
          const allForDay = [
            ...eventsForDay.map(ev => ({ ...ev, _isTask: false })),
            ...tasksForDay.map(task => ({ ...task, _isTask: true }))
          ];
          if (allForDay.length > 0) {
            console.log(`[AGENDA][MES] Día: ${d.toDateString()} - Eventos:`, allForDay);
          }
        }
      });
    });
    return (
      <div className="w-full h-full flex flex-col justify-between">
        <div className="grid grid-cols-7 text-center text-xs font-semibold text-gray-500 mb-2">
          {dayLabels.map((d, i) => <div key={i}>{d}</div>)}
        </div>
        <div className="flex-1 flex flex-col gap-1">
          {weeks.map((w, i) => (
            <div className="grid grid-cols-7 gap-2 flex-1" key={i}>
              {w.map((d, j) => (
                <div
                  key={j}
                  onClick={() => handleDayClick(d)}
                  className={`h-20 md:h-28 flex flex-col items-end justify-start p-2 border border-gray-100 rounded-xl transition-all duration-150 cursor-pointer select-none
                    ${d && d.toDateString() === getToday().toDateString() ? 'bg-red-200 font-bold text-red-700 shadow-lg border-red-300' : ''}
                    ${d ? 'hover:bg-red-50 hover:scale-[1.03]' : 'bg-transparent border-none'}
                  `}
                >
                  <span className="text-base md:text-lg mb-1">{d ? d.getDate() : ''}</span>
                  {/* Eventos del día */}
                  {d && (() => {
                    const tasksForDay = assignedTasks.filter(task => eventOccursOnDay(task, d));
                    const eventsForDay = events.filter(ev => eventOccursOnDay(ev, d));
                    const allForDay = [
                      ...eventsForDay.map(ev => ({ ...ev, _isTask: false })),
                      ...tasksForDay.map(task => ({ ...task, _isTask: true }))
                    ];
                    return (
                      <div className={allForDay.length > 3 ? 'overflow-y-auto max-h-24 pr-1 scroll-smooth w-full min-w-0 box-border' : 'w-full min-w-0 box-border'}>
                        {allForDay.map((ev: any) => (
                          <div
                            key={ev.id}
                            className={`w-full min-w-0 box-border rounded-lg px-2 py-1 mt-1 text-xs font-semibold truncate flex items-center gap-2 border border-white/30 shadow backdrop-blur cursor-pointer transition-all duration-150 
                              ${ev._isTask ? 'bg-red-600/40 text-white' : CATEGORY_BG_GLASS[ev.category] || 'bg-white/30'}`}
                            title={ev.title}
                            onClick={() => ev._isTask ? navigate(`/worker-projects/${ev.projectId}/tasks/${ev.id}`) : handleEventClick(ev)}
                          >
                            {ev._isTask ? (
                              <FaTasks className="inline-block text-white text-base mr-1" />
                            ) : (
                              <span className={`w-2 h-2 rounded-full ${CATEGORIES.find(c => c.value === ev.category)?.color || 'bg-orange-500'}`}></span>
                            )}
                            <span className={`truncate font-bold text-xs leading-tight ${ev._isTask ? 'text-white' : 'text-black'}`} style={{maxWidth: '100%'}}>{ev.title}</span>
                            {ev.location && typeof ev.location === 'object' && ev.location.address && (
                              <span className="truncate text-[10px] text-white/80 ml-1">{ev.location.address}</span>
                            )}
                            {ev.allDay && !ev._isTask ? <span className="ml-1 text-xs font-normal text-gray-700">({t('workerAgenda.allDay', 'Todo el día')})</span> : null}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderWeek = () => {
    const weekDays = getWeekDays(currentDate);
    weekDays.forEach((d) => {
      const tasksForDay = assignedTasks.filter(task => eventOccursOnDay(task, d));
      const eventsForDay = events.filter(ev => eventOccursOnDay(ev, d));
      const allForDay = [
        ...eventsForDay.map(ev => ({ ...ev, _isTask: false })),
        ...tasksForDay.map(task => ({ ...task, _isTask: true }))
      ];
      if (allForDay.length > 0) {
        console.log(`[AGENDA][SEMANA] Día: ${d.toDateString()} - Eventos:`, allForDay);
      }
    });
    return (
      <div className="w-full h-full flex flex-col justify-between">
        <div className="grid grid-cols-7 text-center text-xs font-semibold text-gray-500 mb-2">
          {dayLabels.map((d, i) => (
            <div key={i} className={weekDays[i].toDateString() === getToday().toDateString() ? 'text-red-700 font-bold' : ''}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2 flex-1">
          {weekDays.map((d, i) => (
            <div
              key={i}
              onClick={() => handleDayClick(d)}
              className={`h-64 md:h-80 border border-gray-100 rounded-xl flex flex-col items-end p-2 transition-all duration-150 cursor-pointer select-none
                ${d.toDateString() === getToday().toDateString() ? 'bg-red-200 font-bold text-red-700 shadow-lg border-red-300' : ''}
                hover:bg-red-50 hover:scale-[1.03]`
              }
            >
              <span className="text-base md:text-lg mb-1">{d.getDate()}</span>
              {/* Eventos del día */}
              {d && (() => {
                const tasksForDay = assignedTasks.filter(task => eventOccursOnDay(task, d));
                console.log('[AGENDA][RENDER WEEK] tasksForDay', d, tasksForDay);
                const eventsForDay = events.filter(ev => eventOccursOnDay(ev, d));
                const allForDay = [
                  ...eventsForDay.map(ev => ({ ...ev, _isTask: false })),
                  ...tasksForDay.map(task => ({ ...task, _isTask: true }))
                ];
                return allForDay.map((ev: any) => {
                  if (ev._isTask) {
                    return (
                      <div
                        key={ev.id}
                        className={`w-full rounded-lg px-2 py-1 mt-1 text-xs font-semibold truncate flex items-center gap-2 border border-white/30 shadow backdrop-blur cursor-pointer transition-all duration-150 bg-red-600/40`}
                        title={ev.title}
                        onClick={() => navigate(`/worker-projects/${ev.projectId}/tasks/${ev.id}`)}
                      >
                        <FaTasks className="inline-block text-black text-base mr-1" />
                        <span className="truncate font-bold text-xs leading-tight text-black" style={{maxWidth: '100%'}}>{ev.title}</span>
                        {ev.location && typeof ev.location === 'object' && ev.location.address && (
                          <span className="truncate text-[10px] text-gray-700 ml-1">{ev.location.address}</span>
                        )}
                        {typeof ev.location === 'string' && (
                          <span className="truncate text-[10px] text-gray-700 ml-1">{ev.location}</span>
                        )}
                      </div>
                    );
                  }
                  // Evento normal
                  return (
                    <div
                      key={ev.id}
                      className={`w-full rounded-lg px-2 py-1 mt-1 text-xs font-semibold truncate flex items-center gap-2 border border-white/30 shadow backdrop-blur cursor-pointer transition-all duration-150 
                        ${CATEGORY_BG_GLASS[ev.category] || 'bg-white/30'}`}
                      title={ev.title}
                      onClick={() => handleEventClick(ev)}
                    >
                      <span className={`w-2 h-2 rounded-full ${CATEGORIES.find(c => c.value === ev.category)?.color || 'bg-orange-500'}`}></span>
                      <span className="truncate font-bold text-xs leading-tight text-black" style={{maxWidth: '100%'}}>{ev.title}</span>
                      {ev.allDay ? <span className="ml-1 text-xs font-normal text-gray-700">({t('workerAgenda.allDay', 'Todo el día')})</span> : null}
                    </div>
                  );
                });
              })()}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDay = () => {
    // Generar las 24 horas del día
    const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
    // Eventos del día (allDay o que cruzan varias horas)
    const dayEvents = events.filter(ev => {
      if (!ev.startDate || !ev.endDate) return false;
      const start = new Date(ev.startDate.toDate());
      const end = new Date(ev.endEndDate ? ev.endEndDate.toDate() : ev.endDate.toDate());
      const dayStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 0, 0, 0, 0);
      const dayEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 23, 59, 59, 999);
      return start <= dayEnd && end >= dayStart;
    });
    // Eventos no allDay para el timeline
    const timelineEvents = events.filter(ev => {
      if (!ev.startDate || !ev.endDate || ev.allDay) return false;
      const start = new Date(ev.startDate.toDate());
      const end = new Date(ev.endEndDate ? ev.endEndDate.toDate() : ev.endDate.toDate());
      const dayStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 0, 0, 0, 0);
      const dayEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 23, 59, 59, 999);
      return start <= dayEnd && end >= dayStart;
    });
    const HOUR_HEIGHT_PX = 48;
    // Unifica eventos y tasks para el timeline
    // Solo tasks del día actual para el timeline
    const dayStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 0, 0, 0, 0);
    const dayEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 23, 59, 59, 999);
    const tasksForDay = assignedTasks.filter(task => {
      const start = getEventDate(task.startDate);
      const end = getEventDate(task.endDate);
      if (!start || !end) return false;
      return start <= dayEnd && end >= dayStart;
    });
    const allTimelineEvents = [...timelineEvents, ...tasksForDay.map(task => ({
      ...task,
      startDate: task.startDate,
      endDate: task.endDate,
      title: task.title || task.name || 'Task',
      description: task.description || '',
      location: task.location || '',
      _isTask: true,
      category: undefined, // fuerza color rojo
    }))];
    console.log('[AGENDA][RENDER DAY] assignedTasks para timeline:', assignedTasks);
    return (
      <div className="flex flex-col w-full h-full flex-1 overflow-hidden">
        <div className="w-full sticky top-0 z-10">
          <div className="backdrop-blur-xl bg-white/70 rounded-t-2xl shadow-lg px-8 py-6 border-b border-red-100 flex items-center justify-center">
            <span className="text-2xl font-bold text-gray-800 tracking-tight capitalize">
              {currentDate.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
        </div>
        {/* Eventos del día (allDay o que cruzan varias horas) */}
        <div className="w-full max-w-2xl mx-auto flex flex-col gap-2 py-4">
          {dayEvents.filter(ev => ev.allDay).length > 0 && (
            <>
              {dayEvents.filter(ev => ev.allDay).map(ev => (
                <div key={ev.id} className={`rounded-lg px-3 py-2 text-xs font-semibold shadow-md truncate flex items-center gap-2 ${CATEGORIES.find(c => c.value === ev.category)?.color || 'bg-red-500'} text-white`} title={ev.title} onClick={() => handleEventClick(ev)}>
                  <span className={`w-2 h-2 rounded-full ${CATEGORIES.find(c => c.value === ev.category)?.color || 'bg-red-500'}`}></span>
                  {ev.title} <span className="ml-1 text-xs font-normal text-white/80">({t('workerAgenda.allDay', 'All day')})</span>
                </div>
              ))}
            </>
          )}
        </div>
        <div className="w-full overflow-y-auto bg-gradient-to-br from-white/80 via-red-50/80 to-white/90 rounded-b-2xl shadow-2xl border border-white/40 pb-36" style={{paddingBottom: '150px', minHeight: '600px', maxHeight: 'calc(100vh - 220px)'}}>
          {/* Contenedor relative solo para el área de horas */}
          <div className="relative w-full" style={{height: 24 * HOUR_HEIGHT_PX}}>
            {(() => {
              const eventsWithColumns = getEventColumnGroups(allTimelineEvents);
              return eventsWithColumns.map(ev => {
                const startUTC = getEventDate(ev.startDate);
                const endUTC = getEventDate(ev.endEndDate ? ev.endEndDate : ev.endDate);
                const startMinutes = Math.max(0, startUTC.getHours() * 60 + startUTC.getMinutes());
                const endMinutes = Math.min(24 * 60, endUTC.getHours() * 60 + endUTC.getMinutes());
                const top = startMinutes * HOUR_HEIGHT_PX / 60;
                const height = Math.max((endMinutes - startMinutes) * HOUR_HEIGHT_PX / 60, 32);
                const totalCols = typeof ev._totalColumns === 'number' ? ev._totalColumns : 1;
                const col = typeof ev._column === 'number' ? ev._column : 0;
                const width = `calc(80% / ${totalCols})`;
                const left = `calc(20% + (${col} * (80% / ${totalCols})))`;
                return (
                  <div
                    key={ev.id}
                    className={`absolute px-2 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 border border-white/30 shadow-lg backdrop-blur group-hover:shadow-2xl transition-all duration-150 cursor-pointer 
                      ${ev._isTask ? 'bg-red-600/40' : CATEGORY_BG_GLASS[ev.category] || 'bg-white/30'} border-2 border-dashed border-black/30`}
                    style={{
                      top: `${top}px`,
                      height: `${height}px`,
                      left,
                      width,
                      zIndex: 30,
                    }}
                    title={ev.title}
                    onClick={() => handleEventClick(ev)}
                  >
                    {ev._isTask ? (
                      <>
                        <FaTasks className="inline-block text-black text-base mr-1" />
                        <div className="flex-1 flex flex-col min-w-0">
                          <span className="truncate font-bold text-sm leading-tight text-black" style={{maxWidth: '100%'}}>{ev.title}</span>
                          {ev.description && (
                            <span className="block text-xs font-normal text-gray-700 truncate w-full mt-0.5" style={{maxWidth: '100%'}}>{ev.description}</span>
                          )}
                          {ev.location && typeof ev.location === 'object' && ev.location.address && (
                            <span className="flex items-center gap-1 text-xs font-medium mt-0.5 text-gray-700">
                              <FaMapMarkerAlt className="inline-block text-gray-700 text-sm" />
                              <span className="truncate" style={{maxWidth: '90%'}}>{ev.location.address}</span>
                            </span>
                          )}
                          {typeof ev.location === 'string' && (
                            <span className="flex items-center gap-1 text-xs font-medium mt-0.5 text-gray-700">
                              <FaMapMarkerAlt className="inline-block text-gray-700 text-sm" />
                              <span className="truncate" style={{maxWidth: '90%'}}>{ev.location}</span>
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <span className={`w-2 h-2 rounded-full ${CATEGORIES.find(c => c.value === ev.category)?.color || 'bg-orange-500'}`}></span>
                        <div className="flex-1 flex flex-col min-w-0">
                          <span className="truncate font-bold text-sm leading-tight text-black" style={{maxWidth: '100%'}}>{ev.title}</span>
                          {ev.description && (
                            <span className="block text-xs font-normal text-gray-700 truncate w-full mt-0.5" style={{maxWidth: '100%'}}>{ev.description}</span>
                          )}
                          {ev.location && typeof ev.location === 'object' && ev.location.address && (
                            <span className="flex items-center gap-1 text-xs font-medium mt-0.5 text-gray-700">
                              <FaMapMarkerAlt className="inline-block text-gray-700 text-sm" />
                              <span className="truncate" style={{maxWidth: '90%'}}>{ev.location.address}</span>
                            </span>
                          )}
                          {typeof ev.location === 'string' && (
                            <span className="flex items-center gap-1 text-xs font-medium mt-0.5 text-gray-700">
                              <FaMapMarkerAlt className="inline-block text-gray-700 text-sm" />
                              <span className="truncate" style={{maxWidth: '90%'}}>{ev.location}</span>
                            </span>
                          )}
                        </div>
                      </>
                    )}
                    <span className={`ml-2 text-xs font-semibold whitespace-nowrap ${ev._isTask ? 'text-black' : 'text-gray-800'}`}>
                      {startUTC.toLocaleTimeString(i18n.language === 'en' ? 'en-US' : 'es-ES', { hour: '2-digit', minute: '2-digit' })}
                      {' - '}
                      {endUTC.toLocaleTimeString(i18n.language === 'en' ? 'en-US' : 'es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                );
              });
            })()}
            {/* Horas */}
            {hours.map((hour) => (
              <div
                key={hour}
                className={`relative flex items-center group px-8 bg-white/80 border-t border-b border-gray-200 transition-all duration-150 group-hover:bg-red-50/80 group-hover:border-t-red-200 group-hover:border-b-red-200`}
                style={{zIndex:1, height: HOUR_HEIGHT_PX}}
              >
                <span className="font-bold font-['Montserrat'] w-20 text-right mr-10 text-gray-400 text-lg select-none drop-shadow-sm z-20 transition-all duration-150 group-hover:bg-red-500 group-hover:text-white group-hover:rounded-xl group-hover:shadow-lg group-hover:px-2 group-hover:py-1" style={{zIndex: 20}}>
                  {hour}
                </span>
                <div className="flex-1 ml-2 px-6 flex items-center" style={{height: '100%'}}></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Mapea color de categoría a fondo glassmorph
  const CATEGORY_BG_GLASS: Record<string, string> = {
    red: 'bg-orange-500/40', // ahora naranja
    blue: 'bg-blue-500/40',
    green: 'bg-green-500/40',
    yellow: 'bg-yellow-400/40',
    purple: 'bg-purple-500/40',
  };

  return (
    <div className="w-screen h-screen min-h-screen min-w-full bg-gradient-to-br from-red-50 via-white to-red-100 font-['Montserrat'] flex items-center justify-center overflow-hidden">
      <div className="w-full h-full max-w-6xl max-h-screen mx-auto my-4 flex flex-col rounded-3xl shadow-2xl bg-white/90 backdrop-blur-lg border border-white/30 overflow-hidden">
        <header className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 px-8 pt-8 pb-4 border-b border-gray-100 bg-gradient-to-r from-white/80 to-red-50 rounded-t-3xl">
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-bold text-gray-800 drop-shadow-sm tracking-tight">{t('myBusinessAgenda', 'My Business Agenda')}</h1>
            <button onClick={() => setShowModal(true)} className="w-10 h-10 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white text-2xl font-bold shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-red-300">
              +
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={goToday} className="px-4 py-1.5 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm hover:bg-red-50 hover:text-red-700 font-semibold shadow-sm transition-all">{t('workerAgenda.today')}</button>
            <button onClick={goPrev} className="p-2 rounded-full hover:bg-red-50 hover:text-red-700 transition-all" aria-label={t('workerAgenda.previous')}>
              {/* Flecha izquierda SVG */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button onClick={goNext} className="p-2 rounded-full hover:bg-red-50 hover:text-red-700 transition-all" aria-label={t('workerAgenda.next')}>
              {/* Flecha derecha SVG */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="flex bg-gray-100 rounded-lg p-1 ml-2 shadow-inner">
              {viewOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setView(opt.value)}
                  className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all duration-150 ${view === opt.value ? 'bg-gradient-to-br from-red-400 to-red-600 text-white shadow-md' : 'text-gray-600 hover:bg-white hover:text-red-700'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </header>
        <div className="px-8 py-4 flex-1 flex flex-col justify-start">
          <div className="mb-4 text-xl font-medium text-gray-800 text-center capitalize">
            {view === 'mes' && currentDate.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-ES', { month: 'long', year: 'numeric' })}
            {view === 'semana' && `${t('workerAgenda.weekOf')} ${getWeekDays(currentDate)[0].toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`}
          </div>
          <div className="flex-1 min-h-0">
            {view === 'mes' && renderMonth()}
            {view === 'semana' && renderWeek()}
            {view === 'día' && renderDay()}
          </div>
        </div>
      </div>
      {/* Modal para crear evento */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative animate-fadeInUp">
            <button onClick={() => { setShowModal(false); setSelectedEvent(null); }} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold">×</button>
            <h2 className="text-2xl font-bold mb-4 text-gray-800">{selectedEvent ? t('workerAgenda.editEvent', 'Editar evento') : t('workerAgenda.addEvent', 'Crear evento')}</h2>
            <label className="block mb-2 text-gray-700 font-semibold">{t('workerAgenda.eventTitle', 'Título')}</label>
            <input type="text" className="w-full mb-4 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-200" value={eventTitle} onChange={e => setEventTitle(e.target.value)} placeholder={t('workerAgenda.eventTitlePlaceholder', 'Título del evento')} />
            <label className="block mb-2 text-gray-700 font-semibold">{t('workerAgenda.eventDescription', 'Descripción')}</label>
            <textarea className="w-full mb-4 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-200" value={eventDescription} onChange={e => setEventDescription(e.target.value)} placeholder={t('workerAgenda.eventDescriptionPlaceholder', 'Descripción del evento')} />
            <label className="block mb-2 text-gray-700 font-semibold">{t('workerAgenda.eventStart', 'Start')}</label>
            <input type="datetime-local" className="w-full mb-4 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-200" value={eventStartDate} onChange={e => setEventStartDate(e.target.value)} disabled={allDay} />
            <label className="block mb-2 text-gray-700 font-semibold">{t('workerAgenda.eventEnd', 'End')}</label>
            <input type="datetime-local" className="w-full mb-4 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-200" value={eventEndDate} onChange={e => setEventEndDate(e.target.value)} disabled={allDay} />
            <label className="block mb-2 text-gray-700 font-semibold">{t('workerAgenda.eventLocation', 'Ubicación')}</label>
            <input type="text" className="w-full mb-4 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-200" value={eventLocation} onChange={e => setEventLocation(e.target.value)} placeholder={t('workerAgenda.eventLocationPlaceholder', 'Ej: Oficina, Zoom, etc.')} />
            <label className="inline-flex items-center mb-6 cursor-pointer select-none">
              <input type="checkbox" className="form-checkbox h-5 w-5 text-red-500 rounded focus:ring-2 focus:ring-red-200" checked={allDay} onChange={e => setAllDay(e.target.checked)} />
              <span className="ml-2 text-gray-700 font-semibold">{t('workerAgenda.allDay', 'All day')}</span>
            </label>
            {error && <div className="mb-4 text-red-500 font-semibold text-sm">{error}</div>}
            <label className="block mb-2 text-gray-700 font-semibold">{t('workerAgenda.eventCategory', 'Categoría')}</label>
            <div className="relative mb-6">
              <select
                value={eventCategory}
                onChange={e => setEventCategory(e.target.value)}
                className="w-full px-4 py-2 pr-10 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-200 appearance-none font-semibold text-gray-700 bg-white"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              <span className={`absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white ${CATEGORIES.find(c => c.value === eventCategory)?.color || 'bg-red-500'}`}></span>
            </div>
            <div className="flex justify-between gap-4 mt-6">
              <button onClick={() => { setShowModal(false); setSelectedEvent(null); }} className="px-5 py-2 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-all">{t('workerAgenda.cancel', 'Cancelar')}</button>
              {selectedEvent && (
                <button onClick={handleDeleteEvent} disabled={saving} className="px-5 py-2 rounded-lg bg-red-100 text-red-600 font-semibold hover:bg-red-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                  {t('workerAgenda.delete', 'Eliminar')}
                </button>
              )}
              <button onClick={handleSaveEvent} disabled={saving || !eventTitle || !!error} className="px-5 py-2 rounded-lg bg-red-500 text-white font-semibold hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                {saving ? t('workerAgenda.saving', 'Guardando...') : t('workerAgenda.save', 'Guardar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessAgenda; 